// AssetMenu — "Convert thread to..." dropdown shown in the thread header.
// Shows up only when there's at least one assistant message in the thread.

import { useEffect, useRef, useState } from 'react';
import type { AssetType } from '../types';
import { AssetIcon } from './AssetIcon';

const ASSETS: { id: AssetType; label: string; sub: string }[] = [
  { id: 'brief',      label: 'Briefing',          sub: 'A short executive summary — findings and next steps.' },
  { id: 'supply_map', label: 'Supply Map',        sub: 'Sourcing ecosystem + ranked options for a critical material.' },
  { id: 'timeline',   label: 'Timeline',          sub: 'Phases as a table with the critical path called out.' },
  { id: 'workflow',   label: 'Workflow',          sub: 'The steps, owners, inputs, and where things can fail.' },
  { id: 'flowchart',  label: 'Flowchart',         sub: 'A diagram of the process or decision tree.' },
  { id: 'notes',      label: 'Compiled Notes',    sub: 'Structured notes — facts, numbers, open questions.' },
  { id: 'report',     label: 'Technical Report',  sub: 'A longer report with summary, findings, and recommendations.' },
];

interface Props {
  disabled?: boolean;
  onSelect: (type: AssetType) => void;
}

export function AssetMenu({ disabled, onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    setTimeout(() => document.addEventListener('mousedown', onDoc), 0);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`inline-flex items-center gap-1.5 text-[12px] font-semibold px-3.5 py-1.5 rounded-md transition-all ${
          disabled
            ? 'bg-bgsoft border border-border text-text3 cursor-not-allowed'
            : open
              ? 'bg-ink text-bg border border-ink shadow-sm'
              : 'bg-ink text-bg border border-ink shadow-sm hover:bg-accent hover:border-accent hover:-translate-y-px hover:shadow-md'
        }`}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
        Convert to…
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${open ? 'rotate-180' : ''}`}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-10 w-80 bg-card border border-border rounded-lg shadow-md py-1.5 z-30">
          <div className="text-[11px] text-text3 px-4 pt-2 pb-2">
            Save this chat as
          </div>
          {ASSETS.map((a) => (
            <button
              key={a.id}
              onClick={() => { setOpen(false); onSelect(a.id); }}
              className="w-full text-left px-4 py-2.5 hover:bg-ink/[0.04] transition-colors flex gap-3 items-start group"
            >
              <span className="mt-0.5 transition-transform group-hover:-translate-y-px">
                <AssetIcon type={a.id} />
              </span>
              <span className="flex flex-col min-w-0">
                <span className="text-[13px] text-ink font-medium">{a.label}</span>
                <span className="text-[11px] text-text2 leading-snug mt-0.5">{a.sub}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
